// The console's health readings, kept pure so the board, the tenant screen and
// their tests read one answer (spec 15 §2.21, §4 S9 and S15; D-611, D-612).
//
// The server decides the dot; nothing here re-judges a tenant. What this file
// owns is how a reading is SHOWN: a strip cell's tone, a basis-point figure as
// a percentage, a lag as a duration. The amber lines are the server's own,
// `HEALTH_LINES` from the contracts package (D-612 ruling 1), so a cell and
// the dot never disagree about the same number.

import { HEALTH_LINES } from "@assessly/contracts/platform/health-lines";
import type { ChipVariant } from "../../../components/status-chip";
import type { HealthDay, HealthSnapshot, HealthSummary } from "./platform-api";

export type Dot = HealthSummary["dot"];
export type HealthReason = HealthSummary["reasons"][number];
export type Tone = "good" | "warn" | "bad" | "none";

/** The server's amber lines (D-612), and the red lines a strip adds. A lag
 * cell turns red where `queue_lag` starts counting toward an alert. */
export const TONE_LINES = {
  errorRateBp: { warn: HEALTH_LINES.amberErrorRateBp, bad: 500 },
  p95Ms: { warn: HEALTH_LINES.amberP95Ms, bad: 5000 },
  lagSeconds: { warn: HEALTH_LINES.amberLagSeconds, bad: HEALTH_LINES.lagAlertSeconds },
  uptimeBp: { good: 9990, warn: 9900 },
} as const;

export function dotVariant(dot: Dot): ChipVariant {
  switch (dot) {
    case "green":
      return "ok";
    case "amber":
      return "warn";
    case "red":
      return "bad";
    default:
      return "neutral";
  }
}

/** A window's reachability: up, down, or not collected. */
export function upTone(s: Pick<HealthSnapshot, "subdomain_up">): Tone {
  if (s.subdomain_up === null) return "none";
  return s.subdomain_up ? "good" : "bad";
}

function lineTone(value: number | null, warn: number, bad: number): Tone {
  if (value === null) return "none";
  if (value >= bad) return "bad";
  if (value >= warn) return "warn";
  return "good";
}

export function errorTone(s: Pick<HealthSnapshot, "http_error_rate_bp" | "request_count">): Tone {
  if (s.request_count === 0) return "none";
  return lineTone(s.http_error_rate_bp, TONE_LINES.errorRateBp.warn, TONE_LINES.errorRateBp.bad);
}

export function latencyTone(s: Pick<HealthSnapshot, "p95_latency_ms">): Tone {
  return lineTone(s.p95_latency_ms, TONE_LINES.p95Ms.warn, TONE_LINES.p95Ms.bad);
}

export function lagTone(s: Pick<HealthSnapshot, "queue_lag_seconds">): Tone {
  return lineTone(s.queue_lag_seconds, TONE_LINES.lagSeconds.warn, TONE_LINES.lagSeconds.bad);
}

export function uptimeTone(bp: number | null): Tone {
  if (bp === null) return "none";
  if (bp >= TONE_LINES.uptimeBp.good) return "good";
  if (bp >= TONE_LINES.uptimeBp.warn) return "warn";
  return "bad";
}

export function dayTone(d: Pick<HealthDay, "uptime_bp">): Tone {
  return uptimeTone(d.uptime_bp);
}

/** Basis points as a percentage: 9990 → "99.90 %" in the viewer's language. */
export function formatBp(bp: number | null, locale: string): string | null {
  if (bp === null) return null;
  return new Intl.NumberFormat(locale, {
    style: "percent",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(bp / 10000);
}

/** A duration in the largest whole unit that keeps it readable. */
export function formatSeconds(seconds: number | null, locale: string): string | null {
  if (seconds === null) return null;
  const [value, unit] =
    seconds >= 86_400
      ? [Math.floor(seconds / 86_400), "day"]
      : seconds >= 3600
        ? [Math.floor(seconds / 3600), "hour"]
        : seconds >= 60
          ? [Math.floor(seconds / 60), "minute"]
          : [seconds, "second"];
  return new Intl.NumberFormat(locale, { style: "unit", unit, unitDisplay: "short" }).format(value);
}

export function formatMs(ms: number | null, locale: string): string | null {
  if (ms === null) return null;
  return new Intl.NumberFormat(locale, {
    style: "unit",
    unit: "millisecond",
    unitDisplay: "short",
  }).format(ms);
}

/** Seconds from `iso` to `now`, never negative; null for no time. */
export function ageSeconds(iso: string | null, now: Date): number | null {
  if (iso === null) return null;
  return Math.max(0, Math.floor((now.getTime() - Date.parse(iso)) / 1000));
}

/** How many of the windows had each tone, for a strip's accessible name. */
export function countTones(tones: readonly Tone[]): Record<Tone, number> {
  const out: Record<Tone, number> = { good: 0, warn: 0, bad: 0, none: 0 };
  for (const t of tones) out[t] += 1;
  return out;
}

/** Whether a staff member may start or stop a drill: a platform_admin of
 * either scope, and only on a sandbox tenant (D-612). The server holds the
 * same line and the staff scope fence on top of it. */
export function canDrill(role: string, tenantSandbox: boolean): boolean {
  return role === "platform_admin" && tenantSandbox;
}
